import { useState } from 'react';
import {
  Zap,
  GitBranch,
  Play,
  Sparkles,
  Mail,
  Bell,
  CheckSquare,
  BarChart3,
  Calendar,
  UserPlus,
  UserCog,
  Banknote,
  Lock,
  X,
  ChevronDown,
  ChevronRight,
  MousePointerClick,
  Database,
  CircleStop,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { WORKFLOW_BLOCKS } from './constants';
import type { WorkflowBlock, WorkflowNodeType } from './types';

const BLOCK_ICONS: Record<WorkflowNodeType, LucideIcon> = {
  trigger: Zap,
  condition: GitBranch,
  action: Play,
  ai: Sparkles,
  end: CircleStop,
};

const BLOCK_KEY_ICONS: Record<string, LucideIcon> = {
  'employee-created': UserPlus,
  'employee-updated': UserCog,
  'payroll-generated': Banknote,
  'payroll-finalized': Lock,
  scheduled: Calendar,
  'manual-test': MousePointerClick,
  'if-condition': GitBranch,
  'salary-threshold': GitBranch,
  'send-email': Mail,
  'send-notification': Bell,
  'create-task': CheckSquare,
  'generate-report': BarChart3,
  'data-loader': Database,
  'generate-ai-summary': Sparkles,
  end: CircleStop,
};

function getBlockIcon(block: WorkflowBlock): LucideIcon {
  return BLOCK_KEY_ICONS[block.blockKey] ?? BLOCK_ICONS[block.type];
}

const DRAG_TYPE = 'application/x-workflow-block';

export interface WorkflowBlocksSidebarProps {
  className?: string;
  onDragStart?: (block: WorkflowBlock) => void;
  /** When set, shows a close button (for overlay mode). */
  onClose?: () => void;
}

const SECTION_KEYS = ['AI Steps', 'Triggers', 'Conditions', 'Actions', 'Flow'] as const;

const defaultOpen: Record<string, boolean> = {
  'AI Steps': true,
  Triggers: true,
  Conditions: true,
  Actions: true,
  Flow: true,
};

export function WorkflowBlocksSidebar({
  className,
  onDragStart,
  onClose,
}: WorkflowBlocksSidebarProps) {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(defaultOpen);

  const handleDragStart = (e: React.DragEvent, block: WorkflowBlock) => {
    e.dataTransfer.setData(DRAG_TYPE, JSON.stringify(block));
    e.dataTransfer.effectAllowed = 'copy';
    onDragStart?.(block);
  };

  const toggleSection = (title: string) => {
    setOpenSections((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  const sections = SECTION_KEYS.map((title) => ({
    title,
    blocks: WORKFLOW_BLOCKS.filter((b) => b.category === title),
  }));

  return (
    <aside
      className={cn(
        'flex w-[280px] shrink-0 flex-col border-r bg-muted/30',
        onClose && 'h-full',
        className
      )}
    >
      <div className="flex shrink-0 items-start justify-between gap-2 border-b px-3 py-3">
        <div>
          <h2 className="text-sm font-medium text-foreground">Blocks</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Drag blocks onto the canvas to build your workflow
          </p>
        </div>
        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={onClose}
            aria-label="Close blocks panel"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      <ScrollArea className="min-h-0 flex-1">
        <div className="space-y-1 p-3">
          {sections.map((section) => {
            const isOpen = openSections[section.title] ?? true;
            return (
              <div key={section.title} className="rounded-md border border-transparent">
                <button
                  type="button"
                  className="flex w-full items-center gap-1.5 rounded-md px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                  onClick={() => toggleSection(section.title)}
                  aria-expanded={isOpen}
                >
                  {isOpen ? (
                    <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                  )}
                  <span>{section.title}</span>
                  <span className="ml-1 text-muted-foreground/70">({section.blocks.length})</span>
                </button>
                {isOpen && (
                  <div className="space-y-1 pt-0.5">
                    {section.blocks.map((block) => {
                      const Icon = getBlockIcon(block);
                      return (
                        <div
                          key={block.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, block)}
                          className={cn(
                            'flex cursor-grab items-start gap-2 rounded-lg border bg-card px-2.5 py-2 text-left transition-colors',
                            'hover:border-primary/40 hover:bg-accent/50 active:cursor-grabbing'
                          )}
                        >
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-foreground">{block.label}</p>
                            {block.description && (
                              <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                                {block.description}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </aside>
  );
}

export { DRAG_TYPE };
